/** Engagement inbox (M4, PRD §51–§55): triaged comments, original + translation, grounded drafts, approve → post. */
import { useState } from "react";
import { navigate, useLocation } from "../../app/router";
import type { Comment } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, Chip, EmptyState, PageHeader, QueryView } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { useComments } from "../../data/queries";
import { languageName, relative } from "../../lib/format";

const TABS = [
  { id: "attention", label: "🔴 Needs attention", status: "needs_attention" },
  { id: "worth", label: "🟡 Worth reviewing", status: "worth_reviewing" },
  { id: "replied", label: "Replied", status: "replied" },
  { id: "all", label: "All", status: "" },
] as const;

export function InboxScreen() {
  const { search } = useLocation();
  const tab = TABS.find((t) => t.id === search.get("tab")) ?? TABS[0];
  const q = useComments(tab.status || undefined);
  return (
    <div className="stack-lg">
      <PageHeader title="Inbox" subtitle="Comments from every account, triaged. No reply is posted without your approval." />
      <div className="chips scroll-x" role="group" aria-label="Filter comments">
        {TABS.map((t) => <Chip key={t.id} pressed={t.id === tab.id} onClick={() => navigate(t.id === "attention" ? "/inbox" : `/inbox?tab=${t.id}`, { replace: true })}>{t.label}</Chip>)}
      </div>
      <QueryView q={q}>{(items) => items.length
        ? <ul className="cards">{items.map((c) => <li key={c.id}><CommentCard c={c} /></li>)}</ul>
        : <EmptyState icon="inbox" title={tab.id === "attention" ? "Nothing needs your attention" : "No comments here"}>Comments are collected from connected accounts every few minutes (M4).</EmptyState>}
      </QueryView>
    </div>
  );
}

function CommentCard({ c }: { c: Comment }) {
  const api = useApi();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(c.draft?.body ?? "");
  const keys = ["comments", "dashboard"];
  const draft = useMutation(() => api.draftReply(c.id), { invalidate: keys, onSuccess: () => toast.ok("Drafting a grounded reply…"), onError: (e) => toast.error(e) });
  const save = useMutation((b: string) => api.updateReply(c.draft!.id, b), { invalidate: keys, onSuccess: () => setEditing(false), onError: (e) => toast.error(e) });
  const act = useMutation((a: "approve" | "discard") => api.replyAction(c.draft!.id, a), { invalidate: keys, onSuccess: () => toast.ok("Done."), onError: (e) => toast.error(e) });
  const pri = c.triage?.priority;
  const rtl = c.language && ["ur", "ar", "fa"].includes(c.language);
  const d = c.draft && c.draft.status !== "discarded" ? c.draft : null;
  return (
    <article className={`comment ${pri === "red" ? "tone-danger" : pri === "yellow" ? "tone-attention" : ""}`}>
      <div className="comment-head">
        {c.triage?.category && <Badge tone={pri === "red" ? "danger" : pri === "yellow" ? "attention" : "neutral"}>{c.triage.category}</Badge>}
        <span className="muted small">{c.author_name ?? "Viewer"} · {c.platform} · {relative(c.published_at)}</span>
      </div>
      <p className="comment-context muted small">{[c.series_title, c.episode_title].filter(Boolean).join(" · ")}</p>
      <blockquote className="comment-text" dir={rtl ? "rtl" : undefined} lang={c.language ?? undefined}>{c.text}</blockquote>
      {c.translation && <p className="comment-translation"><span className="muted small">{languageName(c.language ?? "")} → English:</span> {c.translation}</p>}
      {c.triage?.reason && <p className="muted small"><Icon name="flag" size={14} /> {c.triage.reason}</p>}

      {d ? (
        <div className="reply">
          <span className="eyebrow">{d.status === "posted" ? "Reply posted" : d.source === "studio" ? "Draft reply · grounded in the episode" : "Your reply"}</span>
          {editing ? (
            <>
              <label className="sr-only" htmlFor={`r-${d.id}`}>Reply</label>
              <textarea id={`r-${d.id}`} rows={4} value={body} onChange={(e) => setBody(e.target.value)} dir={rtl ? "auto" : undefined} />
              <div className="row"><Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button><Button variant="primary" busy={save.pending} disabled={!body.trim()} onClick={() => void save.mutate(body.trim())}>Save</Button></div>
            </>
          ) : (
            <>
              <p className="reply-body" dir={rtl ? "auto" : undefined}>{d.body}</p>
              {d.body_translation && <p className="comment-translation"><span className="muted small">English:</span> {d.body_translation}</p>}
            </>
          )}
          {d.status === "draft" && !editing && (
            <div className="row">
              <Button variant="primary" icon="send" busy={act.pending} onClick={() => void act.mutate("approve")}>Approve &amp; post</Button>
              <Button variant="secondary" icon="edit" onClick={() => { setBody(d.body); setEditing(true); }}>Edit</Button>
              <Button variant="ghost" onClick={() => void act.mutate("discard")}>Discard</Button>
            </div>
          )}
        </div>
      ) : c.status !== "replied" && (
        <div className="row"><Button variant="secondary" icon="sparkle" busy={draft.pending} onClick={() => void draft.mutate()}>Draft a reply</Button></div>
      )}
    </article>
  );
}
